import { inject, Injectable } from '@angular/core'
import type { Observable } from 'rxjs'
import { EMPTY } from 'rxjs'
import { catchError, map, switchMap } from 'rxjs/operators'

import { ActionCableService } from '@app/modules/action-cable'
import { AuthStore } from '@app/modules/auth/store/auth.store'
import { ErrorHandlers } from '@app/services/error-handlers.service'
import { HttpService } from '@app/services/http.service'

import type { PurchaseStatusData } from '../models/purchase-status-data.model'
import type { PurchaseStatusResponse } from '../models/purchase-status-response.model'

@Injectable({ providedIn: 'root' })
export class PurchaseStatusService {
  private readonly httpService = inject(HttpService)
  private readonly actionCableService = inject(ActionCableService)
  private readonly authStore = inject(AuthStore)

  readonly purchaseComplete$ = this.authStore.authenticationSuccess$.pipe(
    switchMap(() =>
      this.actionCableService.getChannel('PurchaseChannel').pipe(switchMap(channel => channel.messages$)),
    ),
  )

  checkPurchaseStatus(): Observable<PurchaseStatusData> {
    return this.httpService.get<PurchaseStatusResponse>('purchases/status').pipe(
      catchError(error => {
        ErrorHandlers.handleError(error)
        return EMPTY
      }),
      map(data => {
        if (data.status === 'not found') {
          return data.locked ? { status: 'locked' } : { status: 'not found' }
        }

        if (data.status === 'pending') {
          return { status: data.status }
        }

        return { status: data.status === 'declined' ? 'error' : data.status, product_id: data.product_id }
      }),
    )
  }
}
